import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { GeneralStatusBadge } from "./GeneralStatusBadge";
import { computeValidity } from "./SaleOverview/StatusValidityCard";
import { formatRoDate } from "../lib/dates";
import {
  ExternalLink,
  Edit,
  ChevronUp,
  ChevronDown,
  Loader2,
  User,
  FileText,
  Building2,
  Shield,
  ShieldOff,
  ShieldX,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const InfoRow = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <span className="text-xs text-slate-500 shrink-0">{label}</span>
    {value ? (
      <span className={`text-xs font-medium text-slate-800 text-right break-all ${mono ? "font-mono" : ""}`}>{value}</span>
    ) : (
      <span className="text-xs text-slate-300 italic">—</span>
    )}
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-1">
    <Icon className="h-3.5 w-3.5" />
    {children}
  </div>
);

// Mini-chip de validitate (versiunea condensată a StatusValidityCard)
const ValidityChip = ({ title, state, label, testId }) => {
  const theme =
    state === "positive"
      ? { cls: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: Shield }
      : state === "negative"
      ? { cls: "bg-red-50 border-red-300 text-red-800", Icon: ShieldX }
      : { cls: "bg-slate-50 border-slate-200 text-slate-600", Icon: ShieldOff };
  const Icon = theme.Icon;
  return (
    <div
      className={`flex-1 min-w-[100px] rounded-md border px-2 py-1.5 ${theme.cls}`}
      title={`${title}: ${label}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-80">
        <Icon className="h-3 w-3" />
        {title}
      </div>
      <p className="mt-0.5 text-[11px] font-semibold truncate">{label}</p>
    </div>
  );
};

const fmtDate = formatRoDate;

const getClientDisplay = (client) => {  if (!client) return "—";
  if (client.client_type === "PF") {
    return client.full_name || `${client.last_name || ""} ${client.first_name || ""}`.trim() || "PF";
  }
  return client.company_name || client.full_name || client.last_name || client.cui || "PJ";
};

export const SalePeekPanel = ({ saleId, onClose, onNavigate, hasPrev, hasNext, position, canEdit }) => {
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = !!saleId;

  useEffect(() => {
    if (!saleId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/sales/${saleId}`)
      .then((r) => { if (!cancelled) setSale(r.data); })
      .catch(() => { if (!cancelled) setSale(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [saleId]);

  // Navigare cu tastatura ↑/↓ între vânzări cât timp panoul e deschis
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); onNavigate(1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); onNavigate(-1); }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [open, onNavigate]);

  const c = sale?.contract || {};
  const cs = sale?.contract_status || {};
  const client = sale?.client;
  const validity = sale ? computeValidity(sale, c, cs) : null;
  const peekReal = (sale?.commercial_agent?.full_name || sale?.agent_name || "").replace(/^INACTIV\s+/i, "").trim();
  const peekUpload = (sale?.raw_agent_full_name || sale?.agent_name || "").replace(/^INACTIV\s+/i, "").trim();
  const peekShowUpload = peekUpload && peekUpload.toUpperCase() !== peekReal.toUpperCase();

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-0"
        data-testid="sale-peek-panel"
      >
        {loading || !sale ? (
          <div className="flex h-full items-center justify-center text-slate-400">
            <SheetTitle className="sr-only">Se încarcă detaliile vânzării</SheetTitle>
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="flex h-full flex-col">
            {/* Header */}
            <SheetHeader className="px-5 pt-5 pb-3 text-left space-y-2 border-b border-slate-100">
              <div className="flex items-start justify-between gap-2 pr-8">
                <div className="min-w-0">
                  <SheetTitle className="text-base font-bold text-slate-900 truncate" data-testid="peek-client-name">
                    {getClientDisplay(client)}
                  </SheetTitle>
                  <p className="text-xs text-slate-500 mt-0.5 font-mono">
                    {client?.cnp || client?.cui || ""}
                    {c.contract_number ? ` · Cont ${c.contract_number}` : ""}
                  </p>
                </div>
                {position?.total > 0 && (
                  <span className="text-[11px] text-slate-400 shrink-0 mt-1" data-testid="peek-position">
                    {position.index + 1} / {position.total}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <GeneralStatusBadge sale={sale} />
                {sale.status && (
                  <Badge variant="outline" className="text-[11px]" data-testid="peek-internal-status">
                    {sale.status}
                  </Badge>
                )}
              </div>
            </SheetHeader>

            {/* Body */}
            <div className="flex-1 px-5 py-3">
              {/* Validitate condensată */}
              {validity && (
                <div className="flex gap-2" data-testid="peek-validity-row">
                  <ValidityChip title="QC PPC" state={validity.qc.state} label={validity.qc.label} testId="peek-validity-qc" />
                  <ValidityChip title="Status SF" state={validity.sf.state} label={validity.sf.label} testId="peek-validity-sf" />
                  <ValidityChip title="Extern" state={validity.ext.state} label={validity.ext.label} testId="peek-validity-ext" />
                </div>
              )}

              <SectionTitle icon={User}>Client</SectionTitle>
              <InfoRow label="Telefon" value={client?.phone_mobile || client?.phone_fixed} />
              <InfoRow label="Email" value={client?.email} />
              <InfoRow label="Județ / Localitate" value={[client?.county, client?.locality].filter(Boolean).join(", ")} />

              <SectionTitle icon={FileText}>Contract</SectionTitle>
              <InfoRow label="Produs" value={c.product_name} />
              <InfoRow label="Tip produs" value={c.product_kind} />
              <InfoRow label="POD" value={c.pod} mono />
              <InfoRow label="Data semnării" value={fmtDate(c.signed_date)} />
              <InfoRow
                label="Stare SF"
                value={
                  sale.salesforce_contract_state ? (
                    <span className="inline-flex items-center gap-1.5">
                      {sale.salesforce_contract_state}
                      {sale.salesforce_contract_state === "Activat" && sale.is_commissionable && (
                        <span className="text-emerald-700">💰</span>
                      )}
                    </span>
                  ) : null
                }
              />
              <InfoRow label="Tip proces" value={sale.case_record_type} />

              <SectionTitle icon={Building2}>Vânzare</SectionTitle>
              <InfoRow label="Agenție" value={sale.agency?.name || sale.commercial_agent?.agency_name} />
              <InfoRow label="Agent real (semnatar)" value={peekReal} />
              {peekShowUpload && <InfoRow label="Agent încărcare" value={peekUpload} />}
              <InfoRow label="Canal" value={sale.channel || sale.commercial_agent?.channel} />
              <InfoRow label="Sursă" value={sale.source_system} />
              <InfoRow label="Data CSD" value={fmtDate(c.csd_date)} />
              <InfoRow label="Lună comisionare (CSD)" value={sale.commission_month} />
              <InfoRow
                label="Creat la"
                value={sale.created_at ? new Date(sale.created_at).toLocaleDateString("ro-RO") : null}
              />

              {(sale.requires_rework || sale.returned || sale.needs_reconciliation || sale.needs_classification) && (
                <>
                  <Separator className="my-3" />
                  <div className="flex flex-wrap gap-1.5" data-testid="peek-flags">
                    {sale.requires_rework && <Badge className="bg-amber-100 text-amber-900 text-[10px]">Rework</Badge>}
                    {sale.returned && <Badge className="bg-amber-100 text-amber-900 text-[10px]">↩️ Returnată</Badge>}
                    {sale.needs_reconciliation && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">⚠ Agent neasociat</Badge>}
                    {sale.needs_classification && <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px]">📋 Incompletă</Badge>}
                  </div>
                </>
              )}
            </div>

            {/* Footer actions */}
            <div className="sticky bottom-0 border-t border-slate-200 bg-white px-5 py-3 flex items-center gap-2">
              <div className="flex items-center gap-1 mr-auto">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onNavigate(-1)}
                  disabled={!hasPrev}
                  title="Vânzarea anterioară (↑)"
                  data-testid="peek-prev"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onNavigate(1)}
                  disabled={!hasNext}
                  title="Vânzarea următoare (↓)"
                  data-testid="peek-next"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              {canEdit && (
                <Link to={`/sales/${sale.id}?edit=1`}>
                  <Button variant="outline" size="sm" data-testid="peek-edit">
                    <Edit className="h-3.5 w-3.5 mr-1.5" />
                    Editează
                  </Button>
                </Link>
              )}
              <Link to={`/sales/${sale.id}`}>
                <Button size="sm" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="peek-open-full">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  Deschide fișa completă
                </Button>
              </Link>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SalePeekPanel;
